/**
 * @description
 * Client service for managing client CRUD operations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { Client, CreateClientRequest, UpdateClientRequest } from "./types";

/**
 * Client service for managing clients
 */
export class ClientService extends BaseAPIClient {
  /**
   * Get all clients
   */
  async getClients(): Promise<Client[]> {
    const data = await this.request<{ clients: Client[] }>("/clients");
    return data.clients;
  }

  /**
   * Get a single client by ID
   */
  async getClient(id: string): Promise<Client> {
    const data = await this.request<{ client: Client }>(`/clients/${id}`);
    return data.client;
  }

  /**
   * Create a new client
   */
  async createClient(data: CreateClientRequest): Promise<Client> {
    const response = await this.request<{ client: Client }>("/clients", {
      method: "POST",
      body: JSON.stringify(data),
    });
    return response.client;
  }

  /**
   * Update an existing client
   */
  async updateClient(id: string, data: UpdateClientRequest): Promise<Client> {
    const response = await this.request<{ client: Client }>(`/clients/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return response.client;
  }

  /**
   * Delete a client
   */
  async deleteClient(id: string): Promise<void> {
    await this.request(`/clients/${id}`, { method: "DELETE" });
  }

  /**
   * Provision D1 database for a client
   */
  async provisionDatabase(clientId: string): Promise<{
    success: boolean;
    databaseId: string;
    databaseName: string;
    message: string;
  }> {
    return this.request<{
      success: boolean;
      databaseId: string;
      databaseName: string;
      message: string;
    }>(`/clients/${clientId}/provision-database`, {
      method: 'POST',
    });
  }

  /**
   * Get domain restriction settings for a client
   */
  async getDomainSettings(clientId: string): Promise<{
    allowedDomains: string[];
    domainRestrictionEnabled: boolean;
  }> {
    return this.request(`/clients/${clientId}/domain-settings`);
  }

  /**
   * Update domain restriction settings for a client
   */
  async updateDomainSettings(
    clientId: string,
    settings: {
      allowedDomains: string[];
      domainRestrictionEnabled: boolean;
    },
  ): Promise<{ success: boolean; message: string }> {
    return this.request(`/clients/${clientId}/domain-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
}

// Export singleton instance
export const clientService = new ClientService();
